import { z } from 'zod';
/**
 * Tickets y visitas.  Modelo definido por el hallazgo H2 (2026-10-11):
 * el ticket ES el servicio, la visita es el acto de llegar a atenderlo.
 */
export const ORIGENES_TICKET = ['TKT_ROLLOUT', 'TKT_ONDEMAND', 'TKT_EXCEDENTE'];
export const ESTADOS_TICKET = [
    'TKT_ABIERTO',
    'TKT_ASIGNADO',
    'TKT_ETA',
    'TKT_ARRIBO',
    'TKT_EN_TRABAJO',
    'TKT_EVIDENCIAS',
    'TKT_CERRADO',
    'TKT_VISITA_FALSA',
    'TKT_CIERRE_ADMINISTRATIVO',
    'TKT_CANCELADO',
];
export const DESENLACES_VISITA = [
    'VIS_PROGRAMADA',
    'VIS_EN_CURSO',
    'VIS_ATENDIDA',
    'VIS_SIN_ACCESO',
    'VIS_REPROGRAMADA',
    'VIS_CANCELADA',
];
/** Estados desde los que ya no se mueve nada. */
export const ESTADOS_TERMINALES = [
    'TKT_CERRADO',
    'TKT_VISITA_FALSA',
    'TKT_CIERRE_ADMINISTRATIVO',
    'TKT_CANCELADO',
];
/**
 * La tabla completa. Lo que no está aquí NO se permite: la autorización de
 * una transición es una lista blanca, no una lista de prohibiciones.
 */
export const TRANSICIONES = [
    {
        desde: 'TKT_ABIERTO',
        hacia: 'TKT_ASIGNADO',
        accion: 'asignar',
        requiere: [
            { campo: 'idc', motivo: 'Elige el IDC que va a atender el servicio' },
            { campo: 'programacion', motivo: 'Programa la fecha y hora de la visita' },
        ],
    },
    { desde: 'TKT_ASIGNADO', hacia: 'TKT_ETA', accion: 'confirmarEnRuta', requiere: [] },
    { desde: 'TKT_ETA', hacia: 'TKT_ARRIBO', accion: 'registrarArribo', requiere: [] },
    /* Del asignado se puede saltar directo al arribo: pasa cuando el IDC llega
       sin reportar la salida, y rechazarlo solo obliga a capturar un ETA falso. */
    { desde: 'TKT_ASIGNADO', hacia: 'TKT_ARRIBO', accion: 'registrarArribo', requiere: [] },
    { desde: 'TKT_ARRIBO', hacia: 'TKT_EN_TRABAJO', accion: 'iniciarTrabajo', requiere: [] },
    { desde: 'TKT_EN_TRABAJO', hacia: 'TKT_EVIDENCIAS', accion: 'pasarAEvidencias', requiere: [] },
    {
        desde: 'TKT_EVIDENCIAS',
        hacia: 'TKT_CERRADO',
        accion: 'cerrarConFirma',
        requiere: [
            { campo: 'evidencia', motivo: 'Carga al menos una evidencia del servicio' },
            { campo: 'inventario', motivo: 'El proyecto exige capturar el inventario antes de cerrar' },
        ],
    },
    /* Sin acceso cierra el SERVICIO, no solo la visita: es la definición de H2.
       Se puede llegar desde cualquier punto en que el IDC ya fue. */
    ...['TKT_ASIGNADO', 'TKT_ETA', 'TKT_ARRIBO'].map((desde) => ({
        desde,
        hacia: 'TKT_VISITA_FALSA',
        accion: 'marcarVisitaFalsa',
        requiere: [
            {
                campo: 'causal',
                motivo: 'Elige la causal: decide a quién se imputa y si consume saldo',
            },
        ],
    })),
    /* Cierre administrativo: se resuelve en escritorio, sin mandar a nadie más. */
    ...['TKT_ABIERTO', 'TKT_ASIGNADO', 'TKT_ETA', 'TKT_ARRIBO', 'TKT_EN_TRABAJO'].map((desde) => ({
        desde,
        hacia: 'TKT_CIERRE_ADMINISTRATIVO',
        accion: 'cerrarAdministrativamente',
        requiere: [{ campo: 'causal', motivo: 'Elige la causal del cierre administrativo' }],
    })),
    ...['TKT_ABIERTO', 'TKT_ASIGNADO', 'TKT_ETA'].map((desde) => ({
        desde,
        hacia: 'TKT_CANCELADO',
        accion: 'cancelar',
        requiere: [{ campo: 'causal', motivo: 'Cancelar exige motivo' }],
    })),
];
/** Las transiciones que salen de un estado. La interfaz arma el combo con esto. */
export function transicionesDesde(estado) {
    return TRANSICIONES.filter((t) => t.desde === estado);
}
export function esTerminal(estado) {
    return ESTADOS_TERMINALES.includes(estado);
}
export function buscarTransicion(desde, hacia) {
    return TRANSICIONES.find((t) => t.desde === desde && t.hacia === hacia);
}
/* ------------------------------- Esquemas -------------------------------- */
export const ticketAlta = z.object({
    cliente_id: z.string().uuid(),
    contrato_id: z.string().uuid(),
    proyecto_id: z.string().uuid().optional(),
    sitio_id: z.string().uuid(),
    tipo_servicio_id: z.string().uuid(),
    origen: z.enum(ORIGENES_TICKET),
    /* Referencia con la que el cliente busca el servicio en SU sistema. */
    referencia_cliente: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9._/-]{1,32}$/)
        .optional(),
    /* Asignación y programación son opcionales al dar de alta: un ticket puede
       nacer abierto y despacharse después. */
    idc_id: z.string().uuid().optional(),
    programada_en: z.string().datetime().optional(),
});
export const esquemaTransicion = z.object({
    hacia: z.enum(ESTADOS_TICKET),
    idc_id: z.string().uuid().optional(),
    programada_en: z.string().datetime().optional(),
    causal_id: z.string().uuid().optional(),
    nota: z.string().trim().max(2000).optional(),
    /* Quién hizo el trabajo, cuando el despachador captura por cuenta del IDC.
       Va a la auditoría: una bitácora que sirve de evidencia tiene que decir
       quién capturó y por quién, no fingir que lo hizo el IDC. */
    a_nombre_de: z.string().trim().max(120).optional(),
});
export const visitaAlta = z.object({
    idc_id: z.string().uuid().optional(),
    programada_en: z.string().datetime(),
    nota: z.string().trim().max(2000).optional(),
});
export const visitaDesenlace = z.object({
    desenlace: z.enum(DESENLACES_VISITA),
    causal_id: z.string().uuid().optional(),
    arribo_en: z.string().datetime().optional(),
    salida_en: z.string().datetime().optional(),
    nota: z.string().trim().max(2000).optional(),
});
//# sourceMappingURL=tickets.js.map